import api from './api';

/**
 * Read-only Warehouse (Whse) tab data for a PRO — mirrors the backend
 * GET /orders/{pronumber}/whse (V1 parity port of getReceivingData / getBLData).
 *
 * The tab has two flavors chosen server-side by the order's movetype:
 *   - receiving (IMPORT/INBOUND): masterrcv summary + receiving (MR) grid
 *   - shipping  (EXPORT/OUTBOUND): masterbl summary + bl grid
 *   - none: any other movetype (V1 doesn't render the tab)
 *
 * Every value is a display-ready string (already formatted by the backend to
 * match V1), except `recno` and the *Open booleans / counts.
 */

export type WhseFlavor = 'receiving' | 'shipping' | 'none';

/** One MR line in the Receiving (import/inbound) grid. */
export interface WhseReceivingRow {
  recno: number;
  rcvNum: string;
  rcvWhse: string;
  rcvPo: string;
  productDescription: string;
  packingListQty: string;
  packingListUom: string;
  rcvMasterQty: string;
  rcvMasterUom: string;
  rcvSecondaryQty: string;
  rcvSecondaryUom: string;
  rcvLbs: string;
  rcvKgs: string;
  consigneeName: string;
  consigneeInfo: string;
  rcvArriveDate: string;
  checkInDatetime: string;
  rcvSplitFrom: string;
  description: string;
  blPronumber: string;
  origMr: string;
  transferMr: string;
  rcvHold: string;
  rcvHoldsOpen: boolean;
  rcvStorageLfdDate: string;
  customerName: string;
  billToName: string;
  overageShortage: string;
  damages: string;
  damagesOpen: boolean;
  rcvShipDate: string;
  deleted: string;
}

/** One BL line in the Shipping (export/outbound) grid. */
export interface WhseShippingRow {
  recno: number;
  blNum: string;
  blWhse: string;
  productCode: string;
  pickType: string;
  pickValue: string;
  pickPolicy: string;
  callOffNum: string;
  onhandMasterQty: string;
  onhandMasterUom: string;
  onhandSecondaryQty: string;
  onhandSecondaryUom: string;
  blQty: string;
  blUom: string;
  blLbs: string;
  blKgs: string;
  blShipDate: string;
  rcvPronumber: string;
  checkInDatetime: string;
  customerName: string;
  deleted: string;
}

export interface WhseReceivingSummary {
  totalQty: string;
  sealNumber: string;
  cargoLbs: string;
  cargoKgs: string;
  whseDoor: string;
  terminal: string;
  cccStandard: string;
  accountType: string;
  receivingType: string;
  outboundMode: string;
  thirdPartySystem: string;
  labelType: string;
  showMrQty: string;
  showMrDetails: string;
  mrHold: string;
}

export interface WhseShippingSummary {
  totalQty: string;
  sealNumber: string;
  cargoLbs: string;
  cargoKgs: string;
  blTerminal: string;
  blCccStandard: string;
  blComments: string;
  whseTransfer: string;
  whseDoor: string;
  blPreload: string;
  blLoadDatetime: string;
  blHold: string;
  thirdPartySystem: string;
}

export type WhseResponse =
  | {
      flavor: 'receiving';
      movetype: string;
      summary: WhseReceivingSummary;
      rows: WhseReceivingRow[];
      whseInstructionsCount: number;
      totalQty: string;
    }
  | {
      flavor: 'shipping';
      movetype: string;
      summary: WhseShippingSummary;
      rows: WhseShippingRow[];
      whseInstructionsCount: number;
      totalQty: string;
    }
  | {
      flavor: 'none';
      movetype: string;
      summary: Record<string, never>;
      rows: never[];
      whseInstructionsCount: number;
      totalQty: string;
    };

/** Fetch the warehouse tab payload for a PRO (numeric or "T"-template pro). */
async function getWhse(pronumber: number | string): Promise<WhseResponse> {
  const r = await api.get<WhseResponse>(`/orders/${pronumber}/whse`);
  return r.data;
}

const whseService = { getWhse };
export default whseService;
